import toastMessage from "../toastMessage";
import formatSelectData from "../formatSelectData";
import { getStorage } from "../storage";
import axios from "axios";
import moment from "moment";
import language from "../../language";
import { defineRole } from "../handleAccessRoles";
import { ROLE_DONORS } from "../../constants/ROLES";
import api from "../api";

const API_URL = process.env.REACT_APP_BBMS_BASE_API;

const sanitizeDonorApiParams = (params = {}) => {
  const filters = { ...params };

  const toIdArray = (value) => {
    if (!Array.isArray(value)) return value;
    return value.map((el) => (typeof el === "object" ? el?.value || el?._id : el));
  };

  if (filters.centers && !filters.center) {
    filters.center = toIdArray(filters.centers);
  }

  if (filters.centerSites && !filters.centerSite) {
    filters.centerSite = toIdArray(filters.centerSites);
  }

  filters.center = toIdArray(filters.center);
  filters.centerSite = toIdArray(filters.centerSite);

  // Avoid leaking UI-only filter payload in query string.
  delete filters.centers;
  delete filters.centerSites;

  // Serialize arrays as JSON strings so backend parser maps to req.query.center/centerSite.
  if (Array.isArray(filters.center)) {
    filters.center = JSON.stringify(filters.center);
  }

  if (Array.isArray(filters.centerSite)) {
    filters.centerSite = JSON.stringify(filters.centerSite);
  }

  return filters;
};

const fetchData = async (lng = "english", params) => {
  const { page, limit, type } = params;
  try {
    const user = await getStorage();

    const canAccess = defineRole({
      roles: user.accessRole,
      menu: ROLE_DONORS,
      operation: "read,readOnAction",
      pass: user.role === "donor" || user.account_type === "donor",
    });

    if (!canAccess) return;

    let filters = sanitizeDonorApiParams({
      ...params,
    });

    delete filters.type;

    if (!filters["blockInfo.blocked"]) {
      filters["blockInfo.blocked"] = false;
    }

    if (filters.id || filters.donorNumber || filters.q || filters.identityDocNumber || filters.phoneNumber) {
      delete filters["blockInfo.blocked"];
      delete filters["startDate"];
      delete filters["endDate"];
    } else if (!filters["startDate"] || !filters["endDate"]) {
      filters["startDate"] = moment()
        .subtract(1, "month")
        .startOf("day")
        .format("YYYY-MM-DD");
      filters["endDate"] = moment().endOf("day").format("YYYY-MM-DD");
    }

    if (filters.q) {
      delete filters["blockInfo.blocked"];
      delete filters["startDate"];
      delete filters["endDate"];
      delete filters["center"];
      delete filters["centerSite"];
    }

    const options = {
      method: "GET",
      url: `${API_URL}/donor`,
      params: {
        ...filters,
      },
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    const queryRes = await api(options);

    let { data, count, totalRecords, totalPages } = queryRes.data;

    if (!data) {
      data = queryRes.data;
    }

    if (type === "dropdown") {
      const dropdownData = Array.isArray(data) ? data : data ? [data] : [];
      return formatSelectData(dropdownData, "donorNumber", "_id");
      // ["donorNumber", "firstName", "lastName"]
    }

    return !page && !limit
      ? queryRes.data
      : { data, count, totalRecords, totalPages };
  } catch (error) {
    toastMessage("error", error);
  }
};

export default fetchData;

export const deleteDonors = async (lng = "english", id) => {
  try {
    const user = await getStorage();

    const canAccess = defineRole({
      roles: user.accessRole,
      menu: ROLE_DONORS,
      operation: "delete",
    });

    if (!canAccess) return;

    const options = {
      method: "DELETE",
      url: `${API_URL}/donor`,
      data: {
        id,
      },
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    await axios(options);

    toastMessage("success", language[lng].success_delete_donors);
  } catch (error) {
    toastMessage("error", error);
  }
};

export const countDonorsPerCategories = async (
  lng = "english",
  params = {},
) => {
  try {
    const user = await getStorage();

    const canAccess = defineRole({
      roles: user.accessRole,
      menu: ROLE_DONORS,
      operation: "read",
    });

    if (!canAccess) return;

    if (params.q) {
      delete params["blockInfo.blocked"];
      delete params["startDate"];
      delete params["endDate"];
      delete params["center"];
      delete params["centerSite"];
    }

    const apiParams = sanitizeDonorApiParams(params);


    const options = {
      method: "GET",
      url: `${API_URL}/donor/countDonorsCategories`,
      params: apiParams,
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    const data = await axios(options);
    return data.data || 0;
  } catch (error) {
    toastMessage("error", error);
  }
};

export const getDonorDashboardCounts = async (
  lng = "english",
  params = {},
) => {
  try {
    const user = await getStorage();

    const canAccess = defineRole({
      roles: user.accessRole,
      menu: ROLE_DONORS,
      operation: "read",
    });

    if (!canAccess) return;

    const apiParams = sanitizeDonorApiParams(params);

    const options = {
      method: "GET",
      url: `${API_URL}/donor/dashboard-counts`,
      params: apiParams,
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    const { data } = await api(options);
    return data || {};
  } catch (error) {
    toastMessage("error", error);
  }
};
